import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '../contexts/UserDataContext';

const BearDen = () => {
  const { user } = useAuth();
  const { userApiKeys } = useUserData();
  const [searchSymbol, setSearchSymbol] = useState('');
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [watchlist, setWatchlist] = useState([]);
  const [researchNotes, setResearchNotes] = useState('');

  // Sample watchlist data
  useEffect(() => {
    const sampleWatchlist = [
      { symbol: 'AAPL', name: 'Apple Inc.', price: 258.45, change: -4.32, changePercent: -1.64 },
      { symbol: 'MSFT', name: 'Microsoft Corporation', price: 415.20, change: 2.15, changePercent: 0.52 },
      { symbol: 'GOOGL', name: 'Alphabet Inc.', price: 142.85, change: -1.25, changePercent: -0.87 },
      { symbol: 'TSLA', name: 'Tesla Inc.', price: 245.30, change: 8.45, changePercent: 3.57 }
    ];
    setWatchlist(sampleWatchlist);
  }, []);

  const fetchStockData = async (symbol) => {
    if (!symbol) return;
    
    setLoading(true);
    try {
      // Use Yahoo Finance API via backend
      const response = await fetch(`http://localhost:3001/api/yahoo-quote/${symbol}`);
      if (response.ok) {
        const data = await response.json();
        setStockData(data);
        setSelectedStock(symbol);
      } else {
        console.error('Failed to fetch stock data');
      }
    } catch (error) {
      console.error('Error fetching stock data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToWatchlist = (stock) => {
    if (!watchlist.find(item => item.symbol === stock.symbol)) {
      setWatchlist([...watchlist, stock]);
    }
  };

  const removeFromWatchlist = (symbol) => {
    setWatchlist(watchlist.filter(item => item.symbol !== symbol));
  };

  const renderOverview = () => (
    <div className="bear-den-overview">
      <div className="search-section">
        <h2>Stock Research Center</h2>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Enter stock symbol (e.g., AAPL, MSFT, GOOGL)"
            value={searchSymbol}
            onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === 'Enter' && fetchStockData(searchSymbol)}
          />
          <button 
            className="btn-primary"
            onClick={() => fetchStockData(searchSymbol)}
            disabled={!searchSymbol || loading}
          >
            {loading ? 'Loading...' : 'Research'}
          </button>
        </div>
      </div>

      <div className="watchlist-section">
        <h3>Your Watchlist</h3>
        <div className="watchlist-grid">
          {watchlist.map(stock => (
            <div key={stock.symbol} className="watchlist-item">
              <div className="stock-info">
                <h4>{stock.symbol}</h4>
                <p>{stock.name}</p>
              </div>
              <div className="stock-price">
                <div className="price">${stock.price}</div>
                <div className={`change ${stock.change >= 0 ? 'positive' : 'negative'}`}>
                  {stock.change >= 0 ? '+' : ''}{stock.change} ({stock.changePercent >= 0 ? '+' : ''}{stock.changePercent}%)
                </div>
              </div>
              <div className="stock-actions">
                <button 
                  className="btn-small"
                  onClick={() => {
                    setSearchSymbol(stock.symbol);
                    fetchStockData(stock.symbol);
                  }}
                >
                  Research
                </button>
                <button 
                  className="btn-small secondary"
                  onClick={() => removeFromWatchlist(stock.symbol)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStockDetails = () => {
    if (!stockData) return null;

    return (
      <div className="stock-details">
        <div className="stock-header">
          <div className="stock-title">
            <h2>{stockData.name} ({stockData.symbol})</h2>
            <p>{stockData.exchange} • {stockData.currency}</p>
          </div>
          <div className="stock-price-large">
            <div className="current-price">${stockData.price}</div>
            <div className={`price-change ${stockData.change >= 0 ? 'positive' : 'negative'}`}>
              {stockData.change >= 0 ? '+' : ''}{stockData.change} ({stockData.changePercent >= 0 ? '+' : ''}{stockData.changePercent}%)
            </div>
          </div>
          <button 
            className="btn-primary"
            onClick={() => addToWatchlist({
              symbol: stockData.symbol,
              name: stockData.name,
              price: stockData.price,
              change: stockData.change,
              changePercent: stockData.changePercent
            })}
          >
            Add to Watchlist
          </button>
        </div>

        <div className="stock-metrics">
          <div className="metric-card">
            <h4>Market Cap</h4>
            <div className="metric-value">
              ${(stockData.marketCap / 1000000000).toFixed(2)}B
            </div>
          </div>
          <div className="metric-card">
            <h4>P/E Ratio</h4>
            <div className="metric-value">{stockData.pe || 'N/A'}</div>
          </div>
          <div className="metric-card">
            <h4>Dividend Yield</h4>
            <div className="metric-value">
              {stockData.dividendYield ? `${(stockData.dividendYield * 100).toFixed(2)}%` : 'N/A'}
            </div>
          </div>
          <div className="metric-card">
            <h4>Volume</h4>
            <div className="metric-value">
              {stockData.volume ? (stockData.volume / 1000000).toFixed(2) + 'M' : 'N/A'}
            </div>
          </div>
        </div>

        <div className="price-range">
          <h3>Price Range</h3>
          <div className="range-grid">
            <div className="range-item">
              <span className="range-label">Open</span>
              <span className="range-value">${stockData.open}</span>
            </div>
            <div className="range-item">
              <span className="range-label">High</span>
              <span className="range-value">${stockData.high}</span>
            </div>
            <div className="range-item">
              <span className="range-label">Low</span>
              <span className="range-value">${stockData.low}</span>
            </div>
            <div className="range-item">
              <span className="range-label">Previous Close</span>
              <span className="range-value">${stockData.previousClose}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTechnicalAnalysis = () => (
    <div className="technical-analysis">
      <h3>Technical Analysis</h3>
      <div className="analysis-placeholder">
        <p>Technical indicators and charts will be displayed here.</p>
        <div className="indicators-grid">
          <div className="indicator-card">
            <h4>RSI (14)</h4>
            <div className="indicator-value">45.2</div>
            <div className="indicator-status neutral">Neutral</div>
          </div>
          <div className="indicator-card">
            <h4>MACD</h4>
            <div className="indicator-value">-0.5</div>
            <div className="indicator-status bearish">Bearish</div>
          </div>
          <div className="indicator-card">
            <h4>Moving Average (50)</h4>
            <div className="indicator-value">$252.30</div>
            <div className="indicator-status bullish">Above Price</div>
          </div>
          <div className="indicator-card">
            <h4>Bollinger Bands</h4>
            <div className="indicator-value">$248-262</div>
            <div className="indicator-status neutral">Within Range</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderResearchNotes = () => (
    <div className="research-notes">
      <h3>Research Notes</h3>
      <textarea
        placeholder="Add your research notes, analysis, and insights about this stock..."
        value={researchNotes}
        onChange={(e) => setResearchNotes(e.target.value)}
        rows="10"
      />
      <div className="notes-actions">
        <button className="btn-primary">Save Notes</button>
        <button className="btn-secondary">Export</button>
      </div>
    </div>
  );

  const renderTabs = () => (
    <div className="bear-den-tabs">
      <button 
        className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
        onClick={() => setActiveTab('overview')}
      >
        Overview
      </button>
      {stockData && (
        <>
          <button 
            className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Stock Details
          </button>
          <button 
            className={`tab-btn ${activeTab === 'technical' ? 'active' : ''}`}
            onClick={() => setActiveTab('technical')}
          >
            Technical Analysis
          </button>
          <button 
            className={`tab-btn ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            Research Notes
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="bear-den-page">
      <div className="page-header">
        <div className="header-content">
          <div className="logo-section">
            <img src={require('../images/bearden.png')} alt="Bear Den Logo" className="page-logo" />
          </div>
          <div className="title-section">
            <h1>Bear Den</h1>
            <p>In-Depth Stock Research & Analysis</p>
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-value">4</span>
                <span className="stat-label">Watchlist Items</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">12</span>
                <span className="stat-label">Research Notes</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">85%</span>
                <span className="stat-label">Accuracy Rate</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {renderTabs()}

      <div className="tab-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'details' && renderStockDetails()}
        {activeTab === 'technical' && renderTechnicalAnalysis()}
        {activeTab === 'notes' && renderResearchNotes()}
      </div>
    </div>
  );
};

export default BearDen;
